import PhoneIcon from '@/components/Icons/PhoneIcon';
import LeadForm from '@/components/LeadForm/LeadForm';
import { useAppContext } from '@/contexts/AppContext';
import { ListingStatus, type Unit } from '@/domain/units';
import { formatDate } from '@/utils/dates';
import { formatPhoneNumber } from '@/utils/phone';
import { Link } from '@tanstack/react-router';
import type { FC } from 'react';

interface Props {
  unit: Unit;
}

const PropertyCTACard: FC<Props> = ({ unit }) => {
  const { community } = useAppContext();
  return (
    <div className="bg-white p-8 rounded-none border border-gray-200 sticky top-20">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Interested in this floor plan?</h3>

      <div className="space-y-4 mb-6">
        <Link
          to="/contact"
          search={{ unitId: unit.id }}
          className="block w-full text-center btn-primary"
        >
          Schedule a Tour
        </Link>
        <Link
          to="/apply"
          search={{ unitId: unit.id }}
          className="block w-full text-center btn-outline"
        >
          Apply
        </Link>
        {community.contactInformation?.phone ? (
          <a
            href={`tel:${community.contactInformation.phone}`}
            className="block w-full text-center btn-secondary"
          >
            <PhoneIcon className="w-5 h-5 inline mr-2" />
            Call or Text: {formatPhoneNumber(community.contactInformation.phone)}
          </a>
        ) : null}
        <Link to="/floor-plans" className="block w-full text-center btn-outline">
          View All Floor Plans
        </Link>
      </div>

      <div className="border-t border-gray-200 pt-6 mb-6">
        <h4 className="font-semibold text-gray-900 mb-4">Get More Information</h4>
        <LeadForm unit={unit} />
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h4 className="font-semibold text-gray-900 mb-3">Quick Info</h4>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex justify-between">
            <span>Available:</span>
            <span className="font-medium">{formatDate(unit.readyDate)}</span>
          </li>
          <li className="flex justify-between">
            <span>Floor Plan ID:</span>
            <span className="font-medium">#{unit.id}</span>
          </li>
          <li className="flex justify-between">
            <span>Status:</span>
            <span
              className={`font-medium ${unit.listingStatus === ListingStatus.ACTIVE ? 'text-sage-600' : 'text-gray-600'}`}
            >
              {unit.listingStatus === ListingStatus.ACTIVE ? 'Available' : 'Coming Soon'}
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PropertyCTACard;
